import AuthenticationContext from 'config/context/authenticationContext';
import GuiContext from 'config/context/guiContext';
import { useCallback, useContext, useEffect, useState } from 'react';
import { testPermissionName, testPrivateKey, testPublicKey } from 'utils/validations';

const initialServerStatus = {
    response: null,
    loading: false,
    error: null
}

export const useFetchPermissions = () => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { updatePermissionsData, setPermissionsLoading } = useContext(AuthenticationContext);

    const {
        selected: { selectedServer, selectedAccount },
        URL,
    } = useContext(GuiContext);

    useEffect(() => {
        if (!selectedServer || !selectedAccount) {
            return;
        }

        const fetchPermissions = async () => {
            setLoading(true);
            setPermissionsLoading(true);
            setError(null);

            try {
                const res = await fetch(`${URL}/api/permissions/get`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        account_name: selectedAccount,
                        server_url: selectedServer,
                    }),
                });

                const data = await res.json();
                setResponse(data);
                updatePermissionsData(data);
                if (!data?.status) {
                    setError(data?.message);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
                setPermissionsLoading(false);
            }
        };

        fetchPermissions();
        // `updatePermissionsData` i `setPermissionsLoading` su postojani —
        // prvi je `useCallback` u kontekstu, drugi je `useState` setter.
    }, [selectedServer, selectedAccount, URL, updatePermissionsData, setPermissionsLoading]);

    return { response, loading, error };

}

export const useCreatePermission = ({
    permissionName,
    permissionParentName,
    permissionParentPrivateKey,
    permissionPublicKey,
}) => {
    const [response, setResponse] = useState(initialServerStatus.response);
    const [loading, setLoading] = useState(initialServerStatus.loading);
    const [error, setError] = useState(initialServerStatus.error);

    const {
        selected: { selectedServer, selectedAccount },
        URL,
    } = useContext(GuiContext);

    const setNewPermission = async () => {

        if (!testPermissionName(permissionName)) {
            setError('Invalid new permission name!');
            return;
        }

        if (!testPublicKey(permissionPublicKey)) {
            setError('Invalid new permission public key!');
            return;
        }

        if (!testPrivateKey(permissionParentPrivateKey)) {
            setError('Invalid parent permission private key!');
            return;
        }

        setError(null);

        setLoading(true);

        try {
            const res = await fetch(`${URL}/api/permissions/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    server_url: selectedServer || "",
                    account_name: selectedAccount || "",
                    permission_name: permissionName,
                    permission_parent_name: permissionParentName,
                    permission_parent_private_key: permissionParentPrivateKey,
                    permission_public_key: permissionPublicKey
                }),
            });

            const data = await res.json();
            setResponse(data);
            if (!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const resetServerStatus = useCallback(() => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }, [])

    return {
        response,
        loading,
        error,
        setNewPermission,
        resetServerStatus
    };
}

export const useSetPermission = ({
    permissionName,
    permissionParentPrivateKey,
    composition
}) => {
    const [response, setResponse] = useState(initialServerStatus.response);
    const [loading, setLoading] = useState(initialServerStatus.loading);
    const [error, setError] = useState(initialServerStatus.error);

    const {
        selected: { selectedServer, selectedAccount },
        URL,
    } = useContext(GuiContext);

    const setPermission = async () => {

        if (!testPermissionName(permissionName)) {
            setError('Invalid permission name!');
            return;
        }

        if (!testPrivateKey(permissionParentPrivateKey)) {
            setError('Invalid parent permission private key!');
            return;
        }

        setError(null);

        setLoading(true);

        try {
            const res = await fetch(`${URL}/api/permissions/set`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    server_url: selectedServer || "",
                    account_name: selectedAccount || "",
                    permission_name: permissionName,
                    private_key: permissionParentPrivateKey,
                    composition: composition
                }),
            });

            const data = await res.json();
            setResponse(data);
            if (!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const resetServerStatus = useCallback(() => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }, [])

    return {
        response,
        loading,
        error,
        setPermission,
        resetServerStatus
    };
}

export const useEditPermission = ({
    permissionName,
    permissionParentName,
    permissionParentPrivateKey,
    permissionPublicKey,
}) => {
    const [response, setResponse] = useState(initialServerStatus.response);
    const [loading, setLoading] = useState(initialServerStatus.loading);
    const [error, setError] = useState(initialServerStatus.error);

    const {
        selected: { selectedServer, selectedAccount },
        URL,
    } = useContext(GuiContext);

    const changeKeypair = async () => {

        if (!testPermissionName(permissionName)) {
            setError('Invalid permission name!');
            return;
        }

        if (!testPublicKey(permissionPublicKey)) {
            setError('Invalid new public key!');
            return;
        }

        if (!testPrivateKey(permissionParentPrivateKey)) {
            setError('Invalid parent permission private key!');
            return;
        }

        setError(null);

        setLoading(true);

        try {
            const res = await fetch(`${URL}/api/permissions/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    server_url: selectedServer || "",
                    account_name: selectedAccount || "",
                    permission_name: permissionName,
                    permission_parent_name: permissionParentName,
                    permission_parent_private_key: permissionParentPrivateKey,
                    permission_public_key: permissionPublicKey
                }),
            });

            const data = await res.json();
            setResponse(data);
            if (!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const resetServerStatus = useCallback(() => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }, [])

    return {
        response,
        loading,
        error,
        changeKeypair,
        resetServerStatus
    };
}

export const useDeletePermission = ({
    permissionName,
    permissionParentPrivateKey,
}) => {
    const [response, setResponse] = useState(initialServerStatus.response);
    const [loading, setLoading] = useState(initialServerStatus.loading);
    const [error, setError] = useState(initialServerStatus.error);

    const {
        selected: { selectedServer, selectedAccount },
        URL,
    } = useContext(GuiContext);

    // Postojana: `DeletePermissionModal` je drži kroz `handleDeletePermission`
    // u `useEffect`-u koji vodi korake dijaloga.
    const deletePermission = useCallback(async () => {
        if (!testPermissionName(permissionName)) {
            setError('Invalid permission name!');
            return;
        }

        if (!testPrivateKey(permissionParentPrivateKey)) {
            setError('Invalid parent permission private key!');
            return;
        }

        setError(null);

        setLoading(true);

        try {
            const res = await fetch(`${URL}/api/permissions/delete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    server_url: selectedServer || "",
                    account_name: selectedAccount || "",
                    permission_name: permissionName,
                    private_key: permissionParentPrivateKey,
                }),
            });

            const data = await res.json();
            setResponse(data);
            if (!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    }, [URL, selectedServer, selectedAccount, permissionName, permissionParentPrivateKey]);

    const resetServerStatus = useCallback(() => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }, [])

    return {
        response,
        loading,
        error,
        deletePermission,
        resetServerStatus
    };
}

export const useUpdatePermissions = () => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { updatePermissionsData } = useContext(AuthenticationContext);

    const {
        selected: { selectedServer, selectedAccount },
        URL,
    } = useContext(GuiContext);

    const updatePermissions = async () => {
        setLoading(true);
        setError(null);

        try {
            const res = await fetch(`${URL}/api/permissions/get`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    account_name: selectedAccount,
                    server_url: selectedServer,
                }),
            });

            const data = await res.json();
            setResponse(data);
            updatePermissionsData(data);
            if(!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return { response, loading, error, updatePermissions };

}